import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useCancelledOrders } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime } from '../../lib/utils';
import { XCircle, ShoppingBag } from 'lucide-react';
export function CancelledOrdersPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    orders,
    loading: ordersLoading
  } = useCancelledOrders(restaurant?.id);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2386">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-2387" />
      </div>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2388">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-2389">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2390">
          <div data-id="element-2391">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-2392">
              <XCircle className="w-6 h-6 text-red-500" data-id="element-2393" />
              Pedidos Cancelados
            </h1>
            <p className="text-gray-500" data-id="element-2394">
              Historial de pedidos cancelados ({orders.length})
            </p>
          </div>
        </div>

        <Card data-id="element-2395">
          <CardHeader data-id="element-2396">
            <CardTitle className="flex items-center gap-2" data-id="element-2397">
              <ShoppingBag className="w-5 h-5 text-gray-400" data-id="element-2398" />
              Cancelados Recientes
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-2399">
            {ordersLoading ? <div className="space-y-4" data-id="element-2400">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-2401" />)}
              </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-2402">
                <table className="w-full text-sm text-left" data-id="element-2403">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-2404">
                    <tr data-id="element-2405">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-2406">Pedido</th>
                      <th className="px-4 py-3" data-id="element-2407">Cliente</th>
                      <th className="px-4 py-3" data-id="element-2408">Teléfono</th>
                      <th className="px-4 py-3" data-id="element-2409">Total</th>
                      <th className="px-4 py-3" data-id="element-2410">Artículos</th>
                      <th className="px-4 py-3" data-id="element-2411">Estado</th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-2412">Fecha</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2413">
                    {orders.map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors" data-id="element-2414">
                        <td className="px-4 py-3" data-id="element-2415">
                          <span className="font-medium text-gray-900" data-id="element-2416">
                            #{order.order_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700" data-id="element-2417">
                          {order.customer_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-2418">
                          {order.customer_phone || '—'}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900" data-id="element-2419">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-2420">
                          {order.items.length}{' '}
                          {order.items.length === 1 ? 'artículo' : 'artículos'}
                        </td>
                        <td className="px-4 py-3" data-id="element-2421">
                          <Badge variant="danger" data-id="element-2422">Cancelado</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-2423">
                          <div className="text-sm" data-id="element-2424">
                            {new Date(order.created_at).toLocaleDateString('es-CR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-2425">
                            {formatRelativeTime(order.created_at)}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-12" data-id="element-2426">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2427">
                  <XCircle className="w-8 h-8 text-gray-300" data-id="element-2428" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1" data-id="element-2429">
                  Sin pedidos cancelados
                </h3>
                <p className="text-sm text-gray-400" data-id="element-2430">
                  No hay pedidos cancelados registrados.
                </p>
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}